import { authConfig } from '@/features/auth/config'
import { getDeviceInstallationId } from '@/shared/browser/device-installation-id'
import type { TranscriptSegment } from '../transcription/types'

const baseUrl = (authConfig.apiBaseUrl || authConfig.mainSiteUrl).replace(/\/+$/, '')
const endpoint = `${baseUrl}/api/subsite/transcription/aliyun`

type ApiResponse = {
  ok: boolean
  data?: {
    taskId?: string
    status: 'transcribing' | 'completed' | 'failed'
    transcript?: {
      sentences: Array<{ text: string; startMs: number; endMs: number }>
      words: Array<{ text: string; startMs: number; endMs: number }>
    }
    error?: string
  }
  message?: string
}

export async function transcribeWithAliyun(
  file: File,
  language?: string,
  onProgress?: (progress: number) => void,
): Promise<TranscriptSegment[]> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('language', language || 'cn')
  const create = await fetch(endpoint, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-Client-Request-Id': crypto.randomUUID(),
      ...(getDeviceInstallationId() ? { 'X-FastCut-Device-Id': getDeviceInstallationId()! } : {}),
    },
    body: formData,
  })
  const created = (await create.json().catch(() => null)) as ApiResponse | null
  if (!create.ok || !created?.data?.taskId)
    throw new Error(created?.message || '无法创建通义听悟转录任务。')
  const taskUrl = `${endpoint}/${created.data.taskId}`
  for (let attempt = 0; attempt < 360; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, 2000))
    const response = await fetch(taskUrl, { credentials: 'include' })
    const result = (await response.json().catch(() => null)) as ApiResponse | null
    if (!response.ok || !result?.data)
      throw new Error(result?.message || '无法获取通义听悟转录状态。')
    onProgress?.(Math.min(0.95, (attempt + 1) / 120))
    if (result.data.status === 'failed') throw new Error(result.data.error || '通义听悟转录失败。')
    if (result.data.status === 'completed' && result.data.transcript) {
      return result.data.transcript.sentences.map((sentence) => ({
        text: sentence.text,
        start: sentence.startMs / 1000,
        end: sentence.endMs / 1000,
        words: result
          .data!.transcript!.words.filter(
            (word) => word.startMs >= sentence.startMs && word.endMs <= sentence.endMs,
          )
          .map((word) => ({ text: word.text, start: word.startMs / 1000, end: word.endMs / 1000 })),
      }))
    }
  }
  throw new Error('通义听悟转录超时。')
}
